"use client";

import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

interface FadeInViewProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  y?: number;
  scale?: number;
  once?: boolean;
}

export default function FadeInView({
  children,
  className,
  delay = 0,
  duration = 0.6,
  y = 24,
  scale,
  once = true,
}: FadeInViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const isInView = useInView(ref, { once, amount: 0.15 });

  const initial = shouldReduceMotion
    ? { opacity: 0 }
    : { opacity: 0, y, scale: scale ?? 1, filter: "blur(4px)" };

  const animate = shouldReduceMotion
    ? { opacity: isInView ? 1 : 0 }
    : {
        opacity: isInView ? 1 : 0,
        y: isInView ? 0 : y,
        scale: isInView ? 1 : (scale ?? 1),
        filter: isInView ? "blur(0px)" : "blur(4px)",
      };

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={initial}
      animate={animate}
      transition={{
        duration: shouldReduceMotion ? 0.15 : duration,
        delay: shouldReduceMotion ? 0 : delay,
        ease: "easeOut",
      }}
      style={{ willChange: "transform, opacity" }}
    >
      {children}
    </motion.div>
  );
}
